// ─────────────────────────────────────────────────────────────
// Détection de la source de trafic d'un visiteur.
// Identifie la provenance réelle : moteurs de recherche, réseaux sociaux,
// messageries (WhatsApp, Instagram, Facebook…), campagnes UTM et liens
// entrants. Utilisée côté navigateur (au chargement) et côté serveur
// (repli sur le referrer) pour alimenter « Sources de trafic ».
// ─────────────────────────────────────────────────────────────

export interface TrafficSource {
  source: string;
  medium: string;
  referrer: string;
}

interface SourceRule {
  key: string;
  label: string;
  pattern: RegExp;
}

const RULES: SourceRule[] = [
  { key: 'google', label: 'Google', pattern: /google\./i },
  { key: 'bing', label: 'Bing', pattern: /bing\./i },
  { key: 'yahoo', label: 'Yahoo', pattern: /yahoo\./i },
  { key: 'duckduckgo', label: 'DuckDuckGo', pattern: /duckduckgo\./i },
  { key: 'yandex', label: 'Yandex', pattern: /yandex\./i },
  { key: 'facebook', label: 'Facebook', pattern: /facebook|fb\.|m\.facebook/i },
  { key: 'instagram', label: 'Instagram', pattern: /instagram/i },
  { key: 'whatsapp', label: 'WhatsApp', pattern: /whatsapp|wa\.me/i },
  { key: 'tiktok', label: 'TikTok', pattern: /tiktok/i },
  { key: 'twitter', label: 'X / Twitter', pattern: /twitter|t\.co|x\.com/i },
  { key: 'linkedin', label: 'LinkedIn', pattern: /linkedin|lnkd\.in/i },
  { key: 'youtube', label: 'YouTube', pattern: /youtube|youtu\.be/i },
  { key: 'pinterest', label: 'Pinterest', pattern: /pinterest\./i },
  { key: 'telegram', label: 'Telegram', pattern: /telegram|t\.me/i },
  { key: 'snapchat', label: 'Snapchat', pattern: /snapchat/i },
  { key: 'email', label: 'Email', pattern: /mail\.|outlook|gmail|newsletter/i },
];

function capitalize(value: string): string {
  const v = (value || '').trim();
  if (!v) return v;
  return v.charAt(0).toUpperCase() + v.slice(1);
}

/** Détecte la source de trafic à partir du referrer, des UTM et du user-agent. */
export function detectTrafficSource(referrerOverride?: string): TrafficSource {
  const referrer = (
    referrerOverride !== undefined
      ? referrerOverride
      : (typeof document !== 'undefined' ? document.referrer : '')
  ) || '';
  const trimmedRef = referrer.trim();

  // 1) Paramètres de campagne UTM (les plus explicites).
  if (typeof window !== 'undefined') {
    try {
      const params = new URLSearchParams(window.location.search);
      const utmSource = (params.get('utm_source') || '').toLowerCase();
      const utmMedium = (params.get('utm_medium') || '').toLowerCase();
      if (utmSource) {
        const rule = RULES.find((r) => r.key === utmSource || r.pattern.test(utmSource));
        return {
          source: rule ? rule.label : capitalize(utmSource),
          medium: utmMedium || 'campagne',
          referrer: trimmedRef,
        };
      }
      if (params.get('fbclid')) return { source: 'Facebook', medium: 'social', referrer: trimmedRef };
      if (params.get('gclid')) return { source: 'Google', medium: 'cpc', referrer: trimmedRef };
      if (params.get('ttclid')) return { source: 'TikTok', medium: 'social', referrer: trimmedRef };
    } catch {
      /* URL non exploitable */
    }
  }

  // 2) Referrer externe.
  if (trimmedRef) {
    let host = trimmedRef;
    try {
      host = new URL(trimmedRef).hostname;
    } catch {
      /* garder la valeur brute */
    }
    const sameHost = typeof window !== 'undefined' && host === window.location.hostname;
    if (!sameHost) {
      const rule = RULES.find((r) => r.pattern.test(trimmedRef));
      if (rule) return { source: rule.label, medium: 'referral', referrer: host };
      return { source: host || trimmedRef, medium: 'referral', referrer: host };
    }
  }

  // 3) Navigateur intégré aux applications (Instagram/Facebook/WhatsApp…).
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return { source: 'Facebook', medium: 'social', referrer: '' };
  if (/Instagram/i.test(ua)) return { source: 'Instagram', medium: 'social', referrer: '' };
  if (/WhatsApp/i.test(ua)) return { source: 'WhatsApp', medium: 'social', referrer: '' };
  if (/TikTok/i.test(ua)) return { source: 'TikTok', medium: 'social', referrer: '' };

  // 4) Accès direct.
  return { source: 'Direct / inconnu', medium: 'direct', referrer: '' };
}